import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ShieldAlert, ShieldCheck, ScrollText, UserCheck, FileText, FileDown,
  Lock, Activity, Loader2, Save, Trash2, Siren,
} from 'lucide-react';
import ComplianceOperations from '@/components/ComplianceOperations';

const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="mb-4 pb-3 border-b border-slate-200">
    <div className="flex items-center gap-2.5">
      {Icon && (
        <div className="p-1.5 rounded-md bg-slate-100">
          <Icon className="h-4 w-4 text-slate-700" />
        </div>
      )}
      <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">{title}</h2>
    </div>
    {description && <p className="text-sm text-slate-500 mt-1.5 ml-8">{description}</p>}
  </div>
);

const COMPLIANCE_DOCS = [
  { name: 'System Security Plan (SSP)', file: 'Schoolzest_SSP.pptx', desc: 'Maps every IM8 control domain to implementation, parameters, and residual risks.' },
  { name: 'Data Protection Impact Assessment', file: 'Schoolzest_DPIA.pptx', desc: 'Personal data flows, risk assessment, and PDPA compliance mapping.' },
  { name: 'Incident Response Plan (IRP)', file: 'Schoolzest_IRP.pptx', desc: 'Data breach detection, containment, PDPC 3-day notification procedures.' },
  { name: 'Statement of Compliance (SOC)', file: 'Schoolzest_SOC_IM8.pptx', desc: 'Full Annex B response mapping 40+ IM8 controls to compliance status.' },
];

// PDPA-aligned right-sized compliance pack — served directly by backend /api endpoints
const PDPA_PACK = [
  { path: 'compliance-brief', name: 'Right-Sized Compliance Brief', desc: '2-page summary of what we collect, what we do not, and why heavy IM8 controls do not apply.' },
  { path: 'dpa', name: 'Data Processing Agreement (DPA)', desc: 'PDPA-compliant DPA template to sign with each school.' },
  { path: 'privacy-policy', name: 'Privacy Policy', desc: 'PDPA-aligned privacy policy for end-users.' },
  { path: 'terms-of-service', name: 'Terms of Service', desc: 'Standard SaaS terms including data ownership and SLA.' },
  { path: 'dptm-application', name: 'DPTM Application Draft', desc: 'Pre-filled responses for PDPC Data Protection Trustmark application.' },
  { path: 'ir-playbook', name: 'Incident Response Playbook', desc: 'IM8 3.1 / 3.8 incident response procedures and severity SLAs.' },
  { path: 'rca-template', name: 'RCA Template', desc: 'Root Cause Analysis form for post-incident reviews.' },
  { path: 'tech-stack', name: 'Technology Stack', desc: 'Full tech stack with privacy-by-design data flows.' },
];

const AUDIT_CATEGORIES = ['all', 'auth', 'configuration', 'operations', 'security', 'ai'];

const CompliancePage = () => {
  const { token } = useContext(AuthContext);

  // DPO + retention subset of config
  const [config, setConfig] = useState({
    dpo_name: '', dpo_email: '', audit_log_retention_days: 365,
  });
  const [saving, setSaving] = useState(false);

  // MFA
  const [mfaStatus, setMfaStatus] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState('all');

  useEffect(() => {
    fetchConfig();
    fetchMfaStatus();
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(prev => ({
        ...prev,
        dpo_name: res.data.dpo_name || '',
        dpo_email: res.data.dpo_email || '',
        audit_log_retention_days: res.data.audit_log_retention_days || 365,
      }));
    } catch { /* ignore for non-admins */ }
  };

  const handleSaveCompliance = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/config`, {
        dpo_name: config.dpo_name || '',
        dpo_email: config.dpo_email || '',
        audit_log_retention_days: config.audit_log_retention_days || 365,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Compliance settings saved');
    } catch {
      toast.error('Failed to save compliance settings');
    } finally {
      setSaving(false);
    }
  };

  // MFA handlers
  const fetchMfaStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/mfa/status`, { headers: { Authorization: `Bearer ${token}` } });
      setMfaStatus(res.data.mfa_enabled);
    } catch { /* ignore */ }
  };

  const handleMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMfaSetupData(res.data);
    } catch { toast.error('Failed to start MFA setup'); }
    finally { setMfaLoading(false); }
  };

  const handleMfaVerifySetup = async () => {
    if (mfaCode.length !== 6) { toast.error('Enter a 6-digit code'); return; }
    setMfaLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/verify-setup`, { code: mfaCode }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('MFA enabled successfully!');
      setMfaStatus(true);
      setMfaSetupData(null);
      setMfaCode('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setMfaLoading(false); }
  };

  const handleMfaDisable = async () => {
    if (mfaCode.length !== 6) { toast.error('Enter your current 6-digit code to disable MFA'); return; }
    setMfaLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: mfaCode }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('MFA disabled');
      setMfaStatus(false);
      setMfaCode('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to disable MFA'); }
    finally { setMfaLoading(false); }
  };

  // Audit handlers
  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/audit-logs?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      setAuditLogs(res.data);
    } catch { /* non-admins ignored */ }
    finally { setAuditLoading(false); }
  };

  const handlePurgeLogs = async () => {
    if (!window.confirm(`This will permanently delete audit logs older than ${config.audit_log_retention_days || 365} days. Continue?`)) return;
    try {
      const res = await axios.post(`${API}/admin/purge-old-audit-logs`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchAuditLogs();
    } catch { toast.error('Failed to purge logs'); }
  };

  const visibleLogs = auditLogs.filter(l => auditFilter === 'all' || l.category === auditFilter);

  return (
    <div data-testid="compliance-page">
      <div className="mb-8 pb-6 border-b-2 border-slate-200">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-lg bg-gradient-to-br from-rose-500 to-rose-700">
            <ShieldAlert className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Compliance</h1>
        </div>
        <p className="text-base text-slate-500">IM8 / PDPA safeguards — data protection, security, and monitoring</p>
      </div>

      <div className="space-y-10">

        {/* ─── Section: Data Protection ─── */}
        <section data-testid="section-data-protection">
          <SectionHeader icon={UserCheck} title="Data Protection" description="DPO designation, log retention, and compliance documentation" />
          <div className="space-y-6">

            {/* DPO Designation & Log Retention */}
            <Card className="dashboard-card p-6" data-testid="dpo-retention-card">
              <div className="flex items-start space-x-4">
                <div className="p-3 rounded-md bg-violet-50">
                  <UserCheck className="h-6 w-6 text-violet-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Data Protection & Log Retention</h3>
                  <p className="text-sm text-slate-500 mb-4">IM8 PM / LM-8: Designate a DPO and configure audit log retention policy.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label className="text-xs text-slate-500">Data Protection Officer (DPO) Name</Label>
                      <Input value={config.dpo_name || ''} onChange={(e) => setConfig({ ...config, dpo_name: e.target.value })}
                        placeholder="e.g. Ms. Sarah Tan" className="mt-1" data-testid="dpo-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">DPO Email</Label>
                      <Input type="email" value={config.dpo_email || ''} onChange={(e) => setConfig({ ...config, dpo_email: e.target.value })}
                        placeholder="e.g. dpo@school.edu" className="mt-1" data-testid="dpo-email-input" />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-end gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Audit Log Retention Period (days)</Label>
                      <p className="text-[11px] text-slate-400 mb-1.5">Logs older than this will be permanently deleted when purged.</p>
                      <Input type="number" min="30" max="3650" value={config.audit_log_retention_days ?? ''}
                        onChange={(e) => setConfig({ ...config, audit_log_retention_days: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                        className="max-w-[150px]" data-testid="retention-days-input" />
                    </div>
                    <Button onClick={handlePurgeLogs} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="purge-logs-button">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Purge Old Logs
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {/* IM8 Compliance Documents */}
            <Card className="dashboard-card p-6" data-testid="compliance-docs-card">
              <div className="flex items-start space-x-4">
                <div className="p-3 rounded-md bg-emerald-50">
                  <FileText className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">IM8 Compliance Documents</h3>
                  <p className="text-sm text-slate-500 mb-4">Download compliance documentation aligned to Singapore Government IM8 ICT&amp;SS Cybersecurity Control Catalog.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {COMPLIANCE_DOCS.map((doc) => (
                      <a key={doc.file} href={`/${doc.file}`} download
                        className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-100 transition-colors group"
                        data-testid={`download-${doc.file.replace('.pptx', '').toLowerCase()}`}>
                        <div className="p-2 bg-white rounded-md border border-slate-200 group-hover:border-emerald-300 transition-colors">
                          <FileDown className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{doc.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{doc.desc}</p>
                          <p className="text-xs text-emerald-600 mt-1 font-medium">{doc.file}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-3">Documents are in PPTX format. Open with PowerPoint, Google Slides, or Keynote. Fill in school-specific fields (names, contacts) before submission.</p>
                </div>
              </div>
            </Card>

            {/* PDPA Right-Sized Compliance Pack */}
            <Card className="dashboard-card p-6" data-testid="pdpa-pack-card">
              <div className="flex items-start space-x-4">
                <div className="p-3 rounded-md bg-blue-50">
                  <FileDown className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">PDPA Compliance Pack (Right-Sized)</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Pre-built PDFs aligned to SchoolZest&apos;s actual data scope (work email, phone, timetable only).
                    Share with prospective schools to short-circuit lengthy compliance reviews.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {PDPA_PACK.map((d) => (
                      <a
                        key={d.path}
                        href={`${API}/${d.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors group"
                        data-testid={`download-pdpa-${d.path}`}
                      >
                        <div className="p-2 bg-white rounded-md border border-slate-200 group-hover:border-blue-300 transition-colors">
                          <FileDown className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{d.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{d.desc}</p>
                          <p className="text-xs text-blue-600 mt-1 font-medium">/api/{d.path}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-3">All PDFs are public — link directly from your sales emails. No login required.</p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* ─── Section: IM8 Operations (3.3, 3.5, 3.12, 9.4) ─── */}
        <section data-testid="section-im8-operations">
          <SectionHeader
            icon={Siren}
            title="IM8 Operations"
            description="Incidents, drills, lessons learned, and weekly log review"
          />
          <ComplianceOperations />
        </section>

        {/* ─── Section: Security ─── */}
        <section data-testid="section-security">
          <SectionHeader icon={Lock} title="Security" description="Multi-factor authentication and access controls" />
          <div className="space-y-6">

            <Card className="dashboard-card p-6" data-testid="mfa-setup-card">
              <div className="flex items-start space-x-4">
                <div className="p-3 rounded-md bg-blue-50">
                  <ShieldCheck className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Two-Factor Authentication (2FA/MFA)</h3>
                  <p className="text-sm text-slate-500 mb-4">IM8 AC-2: Require MFA for privileged admin accounts. Uses TOTP (Google Authenticator, Authy, etc.)</p>

                  <div className="flex items-center gap-3 mb-4 p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className={`w-3 h-3 rounded-full ${mfaStatus ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className="text-sm font-medium text-slate-700">
                      MFA is {mfaStatus ? <span className="text-emerald-600">Enabled</span> : <span className="text-slate-500">Disabled</span>}
                    </span>
                  </div>

                  {!mfaStatus && !mfaSetupData && (
                    <Button onClick={handleMfaSetup} disabled={mfaLoading} variant="outline" data-testid="mfa-enable-button">
                      {mfaLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Setting up...</> : 'Enable MFA'}
                    </Button>
                  )}

                  {mfaSetupData && (
                    <div className="space-y-4" data-testid="mfa-setup-flow">
                      <div className="flex flex-col sm:flex-row gap-4 items-start">
                        <div className="bg-white p-2 border border-slate-200 rounded-lg">
                          <img src={mfaSetupData.qr_code} alt="MFA QR Code" className="w-48 h-48" data-testid="mfa-qr-code" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-700 mb-2">1. Open your authenticator app (Google Authenticator, Authy, etc.)</p>
                          <p className="text-sm text-slate-700 mb-2">2. Scan the QR code or enter the key manually:</p>
                          <code className="block text-xs bg-slate-100 p-2 rounded font-mono break-all mb-3" data-testid="mfa-secret-key">{mfaSetupData.secret}</code>
                          <p className="text-sm text-slate-700 mb-2">3. Enter the 6-digit code from your app:</p>
                          <div className="flex gap-2">
                            <Input type="text" inputMode="numeric" maxLength={6} value={mfaCode}
                              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                              placeholder="000000" className="w-32 text-center font-mono text-lg tracking-widest"
                              data-testid="mfa-setup-code-input" />
                            <Button onClick={handleMfaVerifySetup} disabled={mfaLoading || mfaCode.length !== 6}
                              className="bg-emerald-600 hover:bg-emerald-700" data-testid="mfa-verify-setup-button">
                              {mfaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Enable'}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <button onClick={() => { setMfaSetupData(null); setMfaCode(''); }}
                        className="text-xs text-slate-500 hover:text-slate-700">Cancel setup</button>
                    </div>
                  )}

                  {mfaStatus && (
                    <div className="space-y-3" data-testid="mfa-disable-section">
                      <p className="text-sm text-slate-600">To disable MFA, enter your current authenticator code:</p>
                      <div className="flex gap-2">
                        <Input type="text" inputMode="numeric" maxLength={6} value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000" className="w-32 text-center font-mono text-lg tracking-widest"
                          data-testid="mfa-disable-code-input" />
                        <Button onClick={handleMfaDisable} disabled={mfaLoading || mfaCode.length !== 6}
                          variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="mfa-disable-button">
                          {mfaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable MFA'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* ─── Section: Monitoring ─── */}
        <section data-testid="section-monitoring">
          <SectionHeader icon={Activity} title="Monitoring" description="Audit trail and system activity logs" />
          <div className="space-y-6">

            <Card className="dashboard-card p-6" data-testid="audit-logs-card">
              <div className="flex items-start space-x-4">
                <div className="p-3 rounded-md bg-slate-100">
                  <ScrollText className="h-6 w-6 text-slate-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Audit Trail</h3>
                      <p className="text-sm text-slate-500">IM8 LM-4: Complete log of system actions for compliance.</p>
                    </div>
                    <Button onClick={fetchAuditLogs} variant="outline" size="sm" disabled={auditLoading} data-testid="audit-refresh-button">
                      {auditLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                    </Button>
                  </div>

                  <div className="flex gap-2 mb-4 flex-wrap">
                    {AUDIT_CATEGORIES.map(cat => (
                      <button key={cat} onClick={() => setAuditFilter(cat)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          auditFilter === cat ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`} data-testid={`audit-filter-${cat}`}>
                        {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div className="overflow-x-auto max-h-96 overflow-y-auto border border-slate-200 rounded-md" data-testid="audit-logs-table">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase">Time</th>
                          <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase">Action</th>
                          <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase">Actor</th>
                          <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleLogs.slice(0, 50).map((log, i) => (
                          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid="audit-log-row">
                            <td className="p-2.5 text-xs text-slate-500 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                            <td className="p-2.5">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                log.category === 'auth' ? 'bg-blue-100 text-blue-800' :
                                log.category === 'security' ? 'bg-red-100 text-red-800' :
                                log.category === 'configuration' ? 'bg-amber-100 text-amber-800' :
                                'bg-slate-100 text-slate-700'
                              }`}>{log.action}</span>
                            </td>
                            <td className="p-2.5 text-xs text-slate-700">{log.actor_name}<br/><span className="text-slate-400">{log.actor_role}</span></td>
                            <td className="p-2.5 text-xs text-slate-500 max-w-xs truncate">{log.ip_address ? `IP: ${log.ip_address}` : ''} {log.details ? JSON.stringify(log.details).slice(0, 80) : ''}</td>
                          </tr>
                        ))}
                        {visibleLogs.length === 0 && (
                          <tr><td colSpan={4} className="p-6 text-center text-sm text-slate-400">No audit logs found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Showing latest {visibleLogs.length} entries</p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveCompliance}
            disabled={saving}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="save-compliance-button"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Compliance Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function CompliancePageGated() {
  return (
    <ComplianceGate>
      <CompliancePage />
    </ComplianceGate>
  );
}

// ─── Compliance Password Gate ─────────────────────────────────────────────
const COMPLIANCE_GATE_KEY = 'sz_compliance_gate_ok';
const COMPLIANCE_PASSPHRASE = 'ComplyDemo123!';

function ComplianceGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(COMPLIANCE_GATE_KEY) === '1');
  const [pwd, setPwd] = useState('');
  const [err, setErr] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (pwd === COMPLIANCE_PASSPHRASE) {
      sessionStorage.setItem(COMPLIANCE_GATE_KEY, '1');
      setUnlocked(true);
      setErr('');
    } else {
      setErr('Incorrect passphrase. Please try again.');
    }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-lg p-8" data-testid="compliance-gate">
        <div className="h-12 w-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-5">
          <Lock className="h-6 w-6" />
        </div>
        <h2 className="font-bold text-xl text-slate-900 mb-2">Compliance Area</h2>
        <p className="text-sm text-slate-600 mb-6">
          This section contains sensitive compliance configuration. Enter the compliance passphrase to continue.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Input
            type="password"
            value={pwd}
            onChange={(e) => { setPwd(e.target.value); setErr(''); }}
            placeholder="Compliance passphrase"
            autoFocus
            data-testid="compliance-gate-input"
          />
          {err && <p className="text-xs text-red-600" data-testid="compliance-gate-error">{err}</p>}
          <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="compliance-gate-submit">
            Unlock Compliance
          </Button>
        </form>
        <p className="text-xs text-slate-400 mt-6">Access expires when this browser tab is closed.</p>
      </div>
    </div>
  );
}
